<template>
  <div>
    <Flash />

    <Loading v-show="loading" />
    <div v-show="!loading" class="l-wrapper">
      <!-- メニュー -->
      <Menu v-if="!isRegistrationRoute && !isInitLoading" :current-user="currentUser"
            :show-more-than-editor-authority="showMoreThanEditorAuthority"
            :show-only-owner-authority="showOnlyOwnerAuthority"
            :show-more-than-operator-authority="showMoreThanOperatorAuthority"
            :has-available-product="hasAvailableProduct"
      />
      <div class="l-container l-container--private">
        <!-- ヘッダー -->
        <header v-if="!isRegistrationRoute" class="l-header">
          <div class="l-header__primary">
            <p class="l-header__siteName">
              <a href="/">
                <KkIcon class="logoIcon" :name="logoName" />
              </a>
            </p>
            <div
              :class="`l-header__hamburger ${ isGlobalMenu ? 'is-open' : 'is-close' }`"
              @click="toggleGlobalMenu(!isGlobalMenu)"
            >
              <span /><span /><span />
            </div>
          </div>
          <div class="l-header__secondary">
            <p class="l-header__companyName">
              {{ currentCompanyName }}
              <span v-if="!isInitLoading" class="l-header__planName" :class="company.attributes.plan">
                {{ $t('labels.plan.' + company.attributes.plan) }}
              </span>
              <span v-if="isAdmin" class="userRole">
                {{ $t('layouts.header.adminUser') }}
              </span>
              <span v-else-if="isDistributer" class="userRole">
                {{ $t('layouts.header.distributorUser') }}
              </span>
            </p>
            <div class="l-header__menu">
              <p ref="name" class="l-header__menu__name" @click="toggleHeaderMenu(!isHeaderMenu)">
                {{ currentUser.email }}
                <Icon name="iconChevronDown" />
              </p>
              <ul :class="`l-header__menu__links ${ isHeaderMenu && 'is-open' }`">
                <li>
                  <a class="js-modal" data-modal="companies" href="#" @click="openModal">
                    {{ $t('layouts.headerMenu.selectCompany.name') }}
                  </a>
                </li>
                <li>
                  <router-link :to="{ name: 'AccountSetting', params: { company_uuid: currentCompanyUuid } }">
                    {{ $t('layouts.headerMenu.accountSetting.name') }}
                  </router-link>
                </li>
                <li v-if="showOnlyOwnerAuthority && isDisplayPaymentSetting && !isDocomoPlan">
                  <router-link :to="{ name: 'PaymentSetting', params: { company_uuid: currentCompanyUuid } }">
                    {{ $t('layouts.headerMenu.paymentSetting.name') }}
                  </router-link>
                </li>
                <li v-if="showOnlyOwnerAuthority && !isDocomoPlan && canConnectToGoogleApi">
                  <router-link :to="{ name: 'CompaniesGoogleOauth', params: { company_uuid: currentCompanyUuid } }">
                    {{ $t('layouts.headerMenu.googleOauth.name') }}
                  </router-link>
                </li>
                <li v-if="providedFeaturesSns && showOnlyOwnerAuthority && !isDocomoPlan">
                  <router-link :to="{ name: 'CompaniesSnsOauth', params: { company_uuid: currentCompanyUuid } }">
                    {{ $t('layouts.headerMenu.snsOauth.name') }}
                  </router-link>
                </li>
                <li v-if="showMoreThanEditorAuthority">
                  <router-link :to="{ name: 'CompaniesManagement', params: { company_uuid: currentCompanyUuid } }">
                    {{ $t('layouts.headerMenu.management.name') }}
                  </router-link>
                </li>
                <li v-if="showMoreThanEditorAuthority">
                  <router-link :to="{ name: 'CompaniesManagementMembers', params: { company_uuid: currentCompanyUuid } }">
                    {{ $t('layouts.headerMenu.managementMembers.name') }}
                  </router-link>
                </li>
                <li>
                  <a class="js-modal" data-modal="locale" href="#" @click="openLocaleModal">
                    {{ $t('layouts.headerMenu.selectLocale.name') }}
                  </a>
                </li>
                <li>
                  <router-link v-if="!isDocomoPlan" :to="{ name: 'CompaniesMailNotificationSetting', params: { company_uuid: currentCompanyUuid } }">
                    {{ $t('layouts.headerMenu.mailNotificationSetting.name') }}
                  </router-link>
                </li>
                <li v-if="showMoreThanEditorAuthority && providedFeaturesCsvExport && !isDocomoPlan">
                  <router-link :to="{ name: 'CompaniesExportResults', params: { company_uuid: currentCompanyUuid } }">
                    {{ $t('layouts.headerMenu.CompaniesExportResults.name') }}
                  </router-link>
                </li>
                <li>
                  <a href="/users/sign_out">
                    {{ $t('layouts.headerMenu.logout.name') }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <ProgressBar v-if="isProgressing || isInitLoading" />
        </header>
        <PublicHeader v-else />

        <!-- コンテンツ -->
        <main v-if="!isInitLoading" class="l-content">
          <h2 v-if="title" class="c-pageTitle">
            {{ title }}
          </h2>
          <Breadcrumb v-if="breadcrumbs" :breadcrumbs="breadcrumbs" />
          <router-view @update-progress="updateProgress" @update-title="updateTitle" @update-breadcrumbs="updateBreadcrumbs" />
        </main>

        <!-- フッター -->
        <Footer v-if="!isInitLoading" :current-user="currentUser" :use-service="useServiceName" />
      </div>
    </div>

    <!-- 会社選択モーダル -->
    <Modal v-if="modal" @close="closeModal">
      <div class="p-modal__content__header">{{ $t('layouts.headerMenu.selectCompany.modal.header') }}</div>
      <div class="p-modal__content__body">
        <ul class="p-companies">
          <li
            v-for="(company, i) in currentUser.invited_companies"
            :key="i"
            class="p-companies__item"
          >
            <p
              class="p-companies__name"
              v-text="company.name"
            />
            <div class="c-button">
              <a
                :href="`/s/companies/${ company.uuid }/dashboards`"
                class="c-button__item c-button__item--primary"
              >
                {{ $t('labels.select') }}
              </a>
            </div>
          </li>
        </ul>
      </div>
    </Modal>

    <!-- 言語設定モーダル -->
    <Modal v-if="localeModal" @close="closeLocaleModal">
      <div class="p-modal__content__header">{{ $t('layouts.headerMenu.selectLocale.modal.header') }}</div>
      <div class="p-modal__content__body">
        <ul class="p-companies">
          <li
            v-for="locale in locales"
            :key="locale.value"
            class="p-companies__item"
          >
            <p
              class="p-companies__name"
              v-text="locale.name"
            />
            <button
              class="c-button"
              @click="changeLocale(locale.value)"
            >
              <span class="c-button__item c-button__item--primary">
                {{ $t('labels.select') }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </Modal>
  </div>
</template>

<script>
import Icon from '../components/Icon.vue'
import Modal from '../components/Modal.vue'
import PublicHeader from '../components/PublicHeader'
import Flash from '../components/Flash'
import Footer from '../components/Footer'
import Loading from '../components/Loading'
import Menu from '../components/Menu'
import Breadcrumb from '../components/Breadcrumb'
import { KkIcon } from '../components/base'

import { mapActions, mapState, mapGetters } from 'vuex'
import ProgressBar from '../components/ProgressBar'

export default {
  name: 'Private',
  components: {
    ProgressBar,
    Footer,
    PublicHeader,
    Icon,
    Modal,
    Flash,
    Loading,
    Menu,
    Breadcrumb,
    KkIcon
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.dynamicTitle = ''
    this.dynamicBreadcrumbs = ''
  },
  data: function() {
    return {
      currentUser: gon.currentUser,
      modal: false,
      localeModal: false,
      flash: null,
      isProgressing: false,
      isInitLoading: true,
      dynamicTitle: '',
      dynamicBreadcrumbs: '',
    }
  },
  computed: {
    ...mapState({
      locale: state => state.locale,
      isHeaderMenu: state => state.layout.isHeaderMenu,
      isGlobalMenu: state => state.layout.isGlobalMenu,
      loading: state => state.companiesAccessibleApi.loading || state.companiesCompany.loading,
      company: state => state.companiesCompany.data,
      companiesAccessibleApi: state => state.companiesAccessibleApi.data,
      companiesAvailableSites: state => state.companiesAvailableSites.data,
    }),
    ...mapGetters({
      unreadGmbLocationSyncLogsCount: 'companiesUserActivity/unreadGmbLocationSyncLogsCount',
      providedFeaturesCsvExport: 'companiesCompany/providedFeaturesCsvExport',
      hasLoadError: 'companiesCompany/hasLoadError',
      providedFeaturesCompetitor: 'companiesCompany/providedFeaturesCompetitor',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
      showMoreThanOperatorAuthority: 'companiesAccessibleApi/showMoreThanOperatorAuthority',
      hasAvailableProduct: 'companiesCompany/hasAvailableProduct',
      providedFeaturesLinkUrl: 'companiesCompany/providedFeaturesLinkUrl',
      useServiceName: 'companiesCompany/useServiceName',
      isAdmin: 'companiesAccessibleApi/isAdmin',
      isDistributer: 'companiesAccessibleApi/isDistributer',
      providedFeaturesSns: 'companiesCompany/providedFeaturesSns',
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
      canConnectToGoogleApi: 'companiesCompany/canConnectToGoogleApi',
    }),
    currentCompanyUuid: function () {
      const company_uuid = this.$route.params.company_uuid
      return company_uuid
    },
    currentCompanyName: function () {
      if (!this.company) return
      return this.company.attributes.name
    },
    isRegistrationRoute() {
      return this.$route.path.includes('registrations')
    },
    locales() {
      return [{ name: '日本語', value: 'ja' }, { name: 'English', value: 'en' }]
    },
    title() {
      if( this.$route.matched.some( obj => obj.meta.title ) ){
        return this.$route.meta.title
      }
      return this.dynamicTitle
    },
    breadcrumbs() {
      if( this.$route.matched.some( obj => obj.meta.breadcrumbs ) ){
        return this.$route.meta.breadcrumbs
      }
      return this.dynamicBreadcrumbs
    },
    logoName() {
      switch(this.useServiceName) {
      case 'kcom':
        return 'logoBlack'
      case 'localone':
        return 'logoLocalOne'
      case 'retty':
        return 'logoRetty'
      default:
        return ''
      }
    },
    isDisplayPaymentSetting() {
      if (!this.company) return

      return this.company.attributes.payment_method === 'credit_card'
    }
  },
  watch: {
    locale() {
      location.reload()
    },
  },
  async created() {
    if (gon.notice) this.$updateFlash({ type: 'notice', message: gon.notice })
    if (gon.alert) this.$updateFlash({ type: 'alert', message: gon.alert })

    await Promise.all([
      this.readAccessibleApi(this.currentCompanyUuid),
      this.readCompany(this.currentCompanyUuid),
      this.readStores(this.currentCompanyUuid),
    ])

    if(this.providedFeaturesLinkUrl) await this.readAvailableSites(this.currentCompanyUuid)

    if (this.hasLoadError) {
      this.$router.push({name: 'Page404'})
      return
    }

    this.isInitLoading = false

    this.readUserActivity({ companyUuid: this.currentCompanyUuid })
  },
  mounted() {
    window.addEventListener('click', this.hideHeaderMenu = e => {
      if (this.$refs.name.contains(e.target)) return
      this.toggleHeaderMenu(false)
    })
  },
  beforeUnmount() {
    window.removeEventListener('click', this.hideHeaderMenu)
  },
  methods: {
    ...mapActions({
      setLocale: 'setLocale',
      toggleHeaderMenu: 'layout/toggleHeaderMenu',
      toggleGlobalMenu: 'layout/toggleGlobalMenu',
      readAccessibleApi: 'companiesAccessibleApi/readAccessibleApi',
      readUserActivity: 'companiesUserActivity/readUserActivity',
      readAvailableSites: 'companiesAvailableSites/readAvailableSites',
      readCompany: 'companiesCompany/readCompany',
      readStores: 'companiesStores/readStores',
    }),
    openModal: function() {
      this.modal = true
    },
    closeModal: function() {
      this.modal = false
    },
    openLocaleModal: function() {
      this.localeModal = true
    },
    closeLocaleModal: function() {
      this.localeModal = false
    },
    changeLocale (value) {
      this.setLocale({ locale: value })
      this.closeLocaleModal()
    },
    updateProgress(value) {
      this.isProgressing = value
    },
    updateTitle(title) {
      this.dynamicTitle = title
    },
    updateBreadcrumbs(breadcrumbs) {
      this.dynamicBreadcrumbs = breadcrumbs
    }
  }
}
</script>

<style lang="scss">
@import '../../../stylesheets/biz_spa/foundation/_var.scss';

.userRole {
  margin-left: 16px;
  font-weight: bold;
  font-size: 12px;
  color: $color-accent-primary;
}
.logoIcon {
  width: 100px;
}

.l-header__siteName {
    display: flex;
    align-items: center;
    max-width: 100px;
  }
</style>
